<script>
  import { dataNazionale } from '../stores/data-nazionale.store.js';
  import { scaleLinear } from 'd3-scale';
  import tippy from 'sveltejs-tippy';

  export let width = 1200;
  export let height = 800;
  export let points;
  export let source = { url: '', desc: '' };

  const props = {
    content: '',
    placement: 'bottom',
    allowHTML: true,
    onShow(instance) {
      const date = dtf.format(new Date(selectedPoint.x));
      let content = '';
      selectedPoint.y.forEach(element => {
        content =
          content +
          '<br/><span style="color: ' +
          element.color +
          '">' +
          element.key +
          ': ' +
          element.value +
          '</span>';
      });
      instance.setContent(`<strong>${date}</strong>${content}`);
    }
  };

  let selectedPoint = {};

  const dtf = new Intl.DateTimeFormat('it', {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit'
  });

  $: updateDate =
    points.length > 0 ? new Date(points[points.length - 1].x) : new Date();

  $: yTicks =
    points.length > 0
      ? [...Array(11).keys()].map(key =>
          Math.floor(
            (Math.max(...points.map(value => value.y[0].value)) / 10) * key
          )
        )
      : [];

  $: xTicks = points.map(entry => entry.x);
  const padding = { top: 20, right: 15, bottom: 20, left: 25 };

  $: xScale = scaleLinear()
    .domain([minX, maxX])
    .range([padding.left, width - padding.right]);

  $: yScale = scaleLinear()
    .domain([Math.min.apply(null, yTicks), Math.max.apply(null, yTicks)])
    .range([height - padding.bottom, padding.top]);

  $: minX = points.length > 0 ? points[0].x : 0;
  $: maxX = points.length > 0 ? points[points.length - 1].x : 0;

  $: paths =
    points.length > 0
      ? [...Array(points[0].y.length).keys()].map(
          num =>
            `M${points
              .map(p => `${xScale(p.x)},${yScale(p.y[num].value)}`)
              .join('L')}`
        )
      : '';

  $: area = `${paths[0]}L${xScale(maxX)},${yScale(0)}L${xScale(minX)},${yScale(
    0
  )}Z`;

  function enter(point) {
    selectedPoint = point;
  }
</script>

<style>
  .chart,
  h2,
  p {
    width: 100%;
    margin-left: auto;
    margin-right: auto;
  }

  svg {
    position: relative;
    width: 100%;
    height: 300px;
    overflow: visible;
  }

  .vertical-test {
    writing-mode: tb-rl;
    transform: rotate(320deg);
  }

  .tick {
    font-size: 0.725em;
    font-weight: 200;
  }

  .tick line {
    stroke: #aaa;
    stroke-dasharray: 2;
  }

  .tick text {
    fill: #666;
    text-anchor: start;
  }

  .tick.tick-0 line {
    stroke-dasharray: 0;
  }

  .x-axis .tick text {
    text-anchor: middle;
  }

  .path-line {
    stroke: white;
    fill: none;
    stroke-linejoin: round;
    stroke-linecap: round;
    stroke-width: 2;
  }

  .path-line-1 {
    stroke: SeaGreen;
  }

  .path-line-2 {
    stroke: DarkGray;
  }

  .path-line-3 {
    stroke: DarkRed;
  }

  .path-line-4 {
    stroke: red;
  }

  .path-area {
    fill: rgba(0, 100, 100, 0.2);
  }
</style>

<small class="mt-2 text-base leading-6 text-gray-500">
  Updated: {dtf.format(updateDate)} Source:
  <a href={source.url}>{source.desc}</a>
</small>

<div class="chart mb-8" bind:clientWidth={width} bind:clientHeight={height}>
  <svg>
    <!-- y axis -->
    <g class="axis y-axis" transform="translate(0, {padding.top})">
      {#each yTicks as tick}
        <g
          class="tick tick-{tick}"
          transform="translate(0, {yScale(tick) - padding.bottom})">
          <line x2="100%" />
          <text y="-4">{tick}</text>
        </g>
      {/each}
    </g>

    <!-- x axis -->
    <g class="axis x-axis">
      {#each xTicks as tick, i}
        {#if width < 500}
          {#if (i % 40 === 0 && (xTicks.length - i) > 20) || i === xTicks.length - 1}
            <g
              class="tick tick-{tick}"
              transform="translate({xScale(tick)},{height})">
              <line y1="-{height}" y2="-{padding.bottom}" x1="0" x2="0" />
              <text class="vertical-test" y="+20">{dtf.format(tick)}</text>
            </g>
          {/if}
        {:else}
          {#if (i % 15 === 0 && (xTicks.length - i)  >  8) || i === xTicks.length - 1}
          <g
            class="tick tick-{tick}"
            transform="translate({xScale(tick)},{height})">
            <line y1="-{height}" y2="-{padding.bottom}" x1="0" x2="0" />
            <text class="vertical-test" y="+20">{dtf.format(tick)}</text>
          </g>
          {/if}
        {/if}
      {/each}
    </g>

    <!-- data -->
    <path class="path-area" d={area} />
    {#each paths as path, i}
      {#if !path.includes('undefined')}
        <path class={'path-line path-line-' + i} d={path} />
      {/if}
    {/each}

    {#each paths as path, i}
      {#each points as point}
        <circle
          r="0.5"
          cx={xScale(point.x)}
          cy={yScale(point.y[i].value)}
          on:mouseenter={() => enter(point)}
          use:tippy={props} />
      {/each}
    {/each}
  </svg>
</div>
